import create from 'zustand';
import { persist } from 'zustand/middleware';
import pick from 'lodash/pick';
import mergeWith from 'lodash/mergeWith';
import type { FormStoreConfig } from './types';

const DEFAULT_INCLUDE_KEYS = [
  'status',
  'formData',
  'pageRoute',
  'currentProvider',
  'lastOfferName',
  'lastIdentifier',
  'leadId',
  'orderConfirmationCode',
];
const DEFAULT_STORAGE_PREFIX = 'upd.tvi.buyflow';

export enum Installation {
  SELF_INSTALL = 'selfInstall',
  PRO_INSTALL_FREE = 'proInstallFree',
  PRO_INSTALL_PAID = 'proInstallPaid',
}

export enum ContractTerm {
  ONE_YEAR = 'oneYear',
  TWO_YEAR = 'twoYear',
  REMOVE = 'remove',
}

export type Form<FormData, BuyPages> = {
  status: 'loading' | 'loaded';
  formData: Partial<FormData>;
  pageRoute: BuyPages;
  currentProvider: string;
  lastOfferName?: string;
  /** Reservation UUID for guest mode and moveId for authenticated mode, used to detect session/offer changes and reset form data accordingly */
  lastIdentifier?: string;
  /** Lead ID from createLead mutation (guest mode) */
  leadId?: string;
  setLeadId: (leadId: string) => void;
  /** Order confirmation code from mutation response (guest mode) */
  orderConfirmationCode?: string;
  setOrderConfirmationCode: (code: string) => void;
  paymentCardTokenize?: () => void;
  setPaymentCardTokenize: ({ tokenize }: { tokenize: () => void }) => void;
  ssnTokenize: () => void;
  setSsnTokenize: ({ tokenize }: { tokenize: () => void }) => void;
  dobTokenize: () => void;
  setDobTokenize: ({ tokenize }: { tokenize: () => void }) => void;
  passcodeTokenize: () => void;
  setPasscodeTokenize: ({ tokenize }: { tokenize: () => void }) => void;
  securityAnswerTokenize: () => void;
  setSecurityAnswerTokenize: ({ tokenize }: { tokenize: () => void }) => void;
  updateFormData: ({ formData }: { formData: Partial<FormData> }) => void;
  setFormData: (updates: Partial<FormData>) => void;
  updateRoute: ({ route }: { route: BuyPages }) => void;
  initializeFormData: ({
    formData,
    offerName,
    identifier,
  }: {
    formData: Partial<FormData>;
    offerName?: string;
    identifier?: string;
  }) => void;
  resetFormData: () => void;
  setOfferName: (name?: string) => void;
};

export function createFormStore<Data, BuyPages>(
  config: FormStoreConfig<Data, BuyPages>
) {
  const {
    provider,
    initialPageRoute,
    initialFormData = {},
    storageKeyPrefix = DEFAULT_STORAGE_PREFIX,
  } = config;

  const storageKey = `${storageKeyPrefix}.${provider.toLowerCase()}`;

  const useStore = create(
    persist<Form<Data, BuyPages>>(
      (set, get) => ({
        status: 'loading',
        formData: initialFormData,
        pageRoute: initialPageRoute,
        currentProvider: provider,
        lastOfferName: undefined,
        lastIdentifier: undefined,
        leadId: undefined,
        setLeadId: (leadId: string) => set(() => ({ leadId })),
        orderConfirmationCode: undefined,
        setOrderConfirmationCode: (code: string) =>
          set(() => ({ orderConfirmationCode: code })),
        // Undefined by default so `buy-flow.tsx` can check if it has been set by the tokenex components.
        paymentCardTokenize: undefined,
        setPaymentCardTokenize: ({ tokenize }) =>
          set(() => ({ paymentCardTokenize: tokenize })),
        ssnTokenize: () => {},
        setSsnTokenize: ({ tokenize }) =>
          set(() => ({ ssnTokenize: tokenize })),
        dobTokenize: () => {},
        setDobTokenize: ({ tokenize }) =>
          set(() => ({ dobTokenize: tokenize })),
        passcodeTokenize: () => {},
        setPasscodeTokenize: ({ tokenize }) =>
          set(() => ({ passcodeTokenize: tokenize })),
        securityAnswerTokenize: () => {},
        setSecurityAnswerTokenize: ({ tokenize }) =>
          set(() => ({ securityAnswerTokenize: tokenize })),
        /**
         * Initialize form data with optional reservation tracking.
         *
         * Resets form data when either identifier or offer name changes,
         * indicating user switched or selected a different offer.
         * Navigation within the same session preserves existing form data.
         *
         * @param formData - Initial or updated form data
         * @param offerName - Current offer name for reset detection
         * @param identifier - Current reservation UUID (guest) or moveId (authenticated) for reset detection
         */
        initializeFormData: ({ formData, offerName, identifier }) => {
          const storedOfferName = get().lastOfferName;
          const storedIdentifier = get().lastIdentifier;

          // Check if offer changed
          const offerChanged =
            offerName && storedOfferName && offerName !== storedOfferName;

          // Check if identifier changed (new session or different user)
          const identifierChanged =
            identifier && storedIdentifier && identifier !== storedIdentifier;

          if (offerChanged || identifierChanged) {
            console.info(
              offerChanged
                ? `Offer changed from ${storedOfferName} to ${offerName}, resetting form`
                : `Identifier changed from ${storedIdentifier} to ${identifier}, resetting form`
            );
            set(() => ({
              status: 'loaded',
              formData: initialFormData,
              lastOfferName: offerName,
              lastIdentifier: identifier,
              leadId: undefined,
              orderConfirmationCode: undefined,
            }));
          } else {
            set(() => ({
              status: 'loaded',
              formData,
              lastOfferName: offerName ?? storedOfferName,
              lastIdentifier: identifier ?? storedIdentifier,
            }));
          }
        },
        /**
         * Legacy API for updating form data. Replaces entire formData object.
         * @param formData - Partial form data to replace existing data
         * @example
         * updateFormData({ formData: { firstName: 'John', lastName: 'Doe' } });
         */
        updateFormData: ({ formData }) => set(() => ({ formData })),

        /**
         * Modern form data updates with intelligent merging.
         *
         * This method handles all form update scenarios:
         * - Flat field updates (phone, email, etc.)
         * - Nested object updates (preserves other nested fields)
         * - Array replacements (arrays are replaced entirely, not merged)
         *
         * Uses lodash mergeWith with custom array handling for the best of both worlds.
         *
         * @param updates - Partial form data object containing fields to update
         * @example
         * // ✅ Single field updates
         * setFormData({ phone: '555-1234' });
         *
         * // ✅ Nested object updates (preserves other fields)
         * setFormData({
         *   creditCard: { firstName: 'Jane' }  // Preserves other creditCard fields
         * });
         *
         * // ✅ Array replacements (arrays are replaced, not merged)
         * setFormData({ smsOptIn: ['sms'] });  // Sets to ['sms']
         * setFormData({ smsOptIn: [] });       // Sets to [] (empty)
         */
        setFormData: (updates: Partial<Data>) =>
          set((state) => {
            // Custom merge: deep merge objects, but replace arrays entirely
            const newFormData = mergeWith(
              {},
              state.formData,
              updates,
              (_objValue, srcValue) => {
                // If source value is an array, replace it entirely (don't merge)
                if (Array.isArray(srcValue)) {
                  return srcValue;
                }
                // Otherwise, let mergeWith handle it (deep merge for objects)
                return undefined;
              }
            );
            return { formData: newFormData };
          }),
        updateRoute: ({ route }) => set(() => ({ pageRoute: route })),
        // can be used to reset the form data in localStorage
        // and tells the store to re-init itself next time it is used
        // by resetting status back to loading
        resetFormData: () =>
          set(() => ({
            formData: initialFormData,
            status: 'loading',
            lastOfferName: undefined,
            lastIdentifier: undefined,
            leadId: undefined,
            orderConfirmationCode: undefined,
          })),
        setOfferName: (name?: string) => set(() => ({ lastOfferName: name })),
      }),
      {
        name: storageKey,
        getStorage: () => sessionStorage,
        partialize: (state) => pick(state, DEFAULT_INCLUDE_KEYS),
      }
    )
  );

  return useStore;
}
